import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Navigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'

// Job-type display labels. Free-text in DB; keep extras here for forward compatibility.
const JOB_TYPE_LABELS = {
  install_aboveground: 'Above-Ground Install',
  install_inground: 'In-Ground Install',
  liner_replacement: 'Liner Replacement',
  liner_inground: 'Liner In-Ground',
  opening: 'Opening',
  closing: 'Closing',
  service: 'Service',
  other: 'Other',
}

function jobTypeLabel(t) {
  if (!t) return 'Other'
  return JOB_TYPE_LABELS[t] || String(t).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function sizeSortKey(s) {
  const m = String(s || '').match(/(\d+(?:\.\d+)?)/)
  return m ? parseFloat(m[1]) : 1e9
}

function formatEt(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    })
  } catch { return '—' }
}

// Same matching rule the leaderboard / SettingsPage use, so the reference panel
// buckets actuals into the same standard the scoreboard would pick.
function matchStandardForJob(job, standards) {
  if (!standards?.length || !job) return null
  const dim = String(job?.pool_dimensions || job?.pool_size || '').toLowerCase().replace(/\s+/g, '')
  if (!dim) return null
  const exact = standards.find(s =>
    (!job?.job_type || s.job_type === job.job_type) &&
    String(s?.pool_size).toLowerCase().replace(/\s+/g, '') === dim
  )
  if (exact) return exact
  return standards.find(s =>
    (!job?.job_type || s.job_type === job.job_type) &&
    dim.indexOf(String(s?.pool_size).toLowerCase().replace(/\s+/g, '')) !== -1
  ) || null
}

export default function TimeStandardsPage() {
  const { profile } = useAuth()
  const isOwner = profile?.role === 'owner'

  const [standards, setStandards] = useState([])
  const [loading, setLoading] = useState(true)
  const [recentJobs, setRecentJobs] = useState([])
  const [recentEntries, setRecentEntries] = useState([])
  const [rowDirty, setRowDirty] = useState({})
  const [rowState, setRowState] = useState({})
  const [savingAll, setSavingAll] = useState(false)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    const [stdRes, jobsRes] = await Promise.all([
      supabase.from('pool_time_standards').select('*'),
      supabase
        .from('jobs')
        .select('id, job_number, client_name, job_type, pool_dimensions, pool_size, completed_at, status')
        .eq('status', 'completed')
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false })
        .limit(10),
    ])
    const stds = stdRes?.data || []
    const jobs = jobsRes?.data || []
    setStandards(stds)
    setRecentJobs(jobs)

    const ids = jobs.map(j => j?.id).filter(Boolean)
    if (ids.length) {
      const { data: entries } = await supabase
        .from('job_time_entries')
        .select('job_id, hours_worked')
        .in('job_id', ids)
        .not('clock_out', 'is', null)
      setRecentEntries(entries || [])
    } else {
      setRecentEntries([])
    }
    setLoading(false)
  }, [])

  useEffect(() => { if (isOwner) fetchAll() }, [fetchAll, isOwner])

  const sorted = useMemo(() => {
    const arr = [...standards]
    arr.sort((a, b) => {
      const la = jobTypeLabel(a?.job_type)
      const lb = jobTypeLabel(b?.job_type)
      if (la !== lb) return la.localeCompare(lb)
      const ka = sizeSortKey(a?.pool_size)
      const kb = sizeSortKey(b?.pool_size)
      if (ka !== kb) return ka - kb
      return String(a?.pool_size || '').localeCompare(String(b?.pool_size || ''))
    })
    return arr
  }, [standards])

  const actualsByJob = useMemo(() => {
    const m = {}
    recentEntries.forEach(e => {
      const h = parseFloat(e?.hours_worked) || 0
      if (h <= 0) return
      m[e.job_id] = (m[e.job_id] || 0) + h
    })
    return m
  }, [recentEntries])

  const dirtyIds = useMemo(() => Object.keys(rowDirty), [rowDirty])

  const saveOne = useCallback(async (id, raw) => {
    const v = parseFloat(raw)
    if (isNaN(v) || v < 0) {
      setRowState(prev => ({ ...prev, [id]: 'error' }))
      return { error: { message: 'Hours must be a non-negative number' } }
    }
    setRowState(prev => ({ ...prev, [id]: 'saving' }))
    const nowIso = new Date().toISOString()
    const { error } = await supabase
      .from('pool_time_standards')
      .update({ expected_hours: v, updated_at: nowIso })
      .eq('id', id)
    if (error) {
      setRowState(prev => ({ ...prev, [id]: 'error' }))
      return { error }
    }
    setStandards(prev => prev.map(r => r?.id === id ? { ...r, expected_hours: v, updated_at: nowIso } : r))
    setRowDirty(prev => { const n = { ...prev }; delete n[id]; return n })
    setRowState(prev => ({ ...prev, [id]: 'saved' }))
    setTimeout(() => {
      setRowState(prev => prev[id] === 'saved' ? (() => { const n = { ...prev }; delete n[id]; return n })() : prev)
    }, 1800)
    return { error: null }
  }, [])

  const saveAll = useCallback(async () => {
    if (!dirtyIds.length) return
    setSavingAll(true)
    for (const id of dirtyIds) {
      // Snapshot the dirty value at call time — saveOne removes it from rowDirty.
      // eslint-disable-next-line no-await-in-loop
      await saveOne(id, rowDirty[id])
    }
    setSavingAll(false)
  }, [dirtyIds, rowDirty, saveOne])

  // 403 redirect — also gated at the route level, but defended in the UI too.
  if (!isOwner) return <Navigate to="/dashboard" replace />
  if (loading) return <div style={s.loading}>Loading time standards…</div>

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <div style={s.title}>POOL TIME STANDARDS</div>
          <div style={s.sub}>Owner-only · Expected crew-hours per pool size + job type. Drives the scoreboard's actual-vs-expected comparison.</div>
        </div>
      </div>

      <div style={s.sectionTitle}>STANDARDS · {sorted.length} ROWS</div>
      <div style={{ overflowX: 'auto', marginBottom: '8px' }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>POOL SIZE</th>
              <th style={s.th}>JOB TYPE</th>
              <th style={{ ...s.th, textAlign: 'right' }}>EXPECTED HOURS</th>
              <th style={s.th}>LAST UPDATED</th>
              <th style={{ ...s.th, width: '90px' }}>STATUS</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => {
              const dirty = Object.prototype.hasOwnProperty.call(rowDirty, r?.id)
              const stateK = rowState[r?.id] || 'idle'
              const displayVal = dirty
                ? rowDirty[r.id]
                : (r?.expected_hours == null ? '' : String(r.expected_hours))
              const inputBorder = stateK === 'error'
                ? '1px solid rgba(224,92,92,0.6)'
                : dirty
                  ? '1px solid rgba(180,140,60,0.5)'
                  : '1px solid rgba(255,255,255,0.1)'
              return (
                <tr key={r?.id} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={s.td}>{r?.pool_size || '—'}</td>
                  <td style={{ ...s.td, color: 'rgba(255,255,255,0.7)' }}>{jobTypeLabel(r?.job_type)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>
                    <input
                      type="number"
                      step="0.25"
                      min="0"
                      value={displayVal}
                      onChange={e => {
                        const v = e.target.value
                        setRowDirty(prev => ({ ...prev, [r.id]: v }))
                        setRowState(prev => {
                          if (prev[r.id] !== 'idle' && prev[r.id] != null) {
                            const n = { ...prev }; delete n[r.id]; return n
                          }
                          return prev
                        })
                      }}
                      onBlur={e => { if (dirty) saveOne(r.id, e.target.value) }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur() } }}
                      style={{ ...s.numInput, border: inputBorder }}
                    />
                    <span style={s.unit}>HRS</span>
                  </td>
                  <td style={{ ...s.td, fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{formatEt(r?.updated_at)}</td>
                  <td style={s.td}>
                    {stateK === 'saving' && <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>SAVING…</span>}
                    {stateK === 'saved' && <span style={{ color: '#4a8c6a', fontSize: '11px' }}>{String.fromCodePoint(0x2713)} SAVED</span>}
                    {stateK === 'error' && <span style={{ color: '#e05c5c', fontSize: '11px' }}>FAILED</span>}
                    {stateK === 'idle' && dirty && <span style={{ color: '#b48c3c', fontSize: '11px' }}>UNSAVED</span>}
                  </td>
                </tr>
              )
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={5} style={s.empty}>No pool_time_standards rows.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '40px' }}>
        <div style={s.sectionTitle}>RECENT ACTUALS · LAST 10 COMPLETED POOL JOBS</div>
        <div style={s.refHelp}>Reference data to calibrate from. Actual hours = sum of <code style={s.code}>job_time_entries.hours_worked</code> per job. Delta &gt; 0 means the crew ran over the expected baseline.</div>
        {recentJobs.length === 0 ? (
          <div style={s.empty}>No completed jobs in history yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>COMPLETED (ET)</th>
                  <th style={s.th}>JOB</th>
                  <th style={s.th}>TYPE · SIZE</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>ACTUAL</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>EXPECTED</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>{String.fromCodePoint(0x0394)}</th>
                </tr>
              </thead>
              <tbody>
                {recentJobs.map(j => {
                  const actual = actualsByJob[j?.id] || 0
                  const std = matchStandardForJob(j, standards)
                  const expected = std?.expected_hours
                  const expNum = expected != null ? parseFloat(expected) : null
                  const delta = (expNum != null && actual > 0) ? (actual - expNum) : null
                  const deltaColor = delta == null
                    ? 'rgba(255,255,255,0.3)'
                    : delta > 0.25 ? '#e05c5c'
                    : delta < -0.25 ? '#4a8c6a'
                    : 'rgba(255,255,255,0.6)'
                  return (
                    <tr key={j?.id} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                      <td style={{ ...s.td, fontSize: '12px', color: 'rgba(255,255,255,0.55)' }}>{formatEt(j?.completed_at)}</td>
                      <td style={s.td}>{j?.job_number || j?.client_name || (j?.id || '').slice(0, 6)}</td>
                      <td style={{ ...s.td, color: 'rgba(255,255,255,0.65)' }}>{jobTypeLabel(j?.job_type)} · {j?.pool_dimensions || j?.pool_size || '—'}</td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 600, color: '#ffffff' }}>{actual > 0 ? actual.toFixed(2) : '—'}</td>
                      <td style={{ ...s.td, textAlign: 'right', color: '#b48c3c' }}>{expNum != null ? expNum.toFixed(2) : '—'}</td>
                      <td style={{ ...s.td, textAlign: 'right', color: deltaColor }}>
                        {delta == null ? '—' : (delta > 0 ? '+' : '') + delta.toFixed(2)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={s.stickyBar}>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
          {dirtyIds.length === 0
            ? 'All changes saved'
            : `${dirtyIds.length} unsaved change${dirtyIds.length === 1 ? '' : 's'}`}
        </div>
        <button
          type="button"
          onClick={saveAll}
          disabled={savingAll || dirtyIds.length === 0}
          style={{ ...s.saveBtn, opacity: (savingAll || dirtyIds.length === 0) ? 0.4 : 1, cursor: (savingAll || dirtyIds.length === 0) ? 'default' : 'pointer' }}
        >
          {savingAll ? 'SAVING…' : 'SAVE ALL'}
        </button>
      </div>
    </div>
  )
}

const s = {
  container: { fontFamily: "'Barlow',sans-serif", maxWidth: '100%', paddingBottom: '90px' },
  loading: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
  title: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  sub: { fontSize: '14px', color: 'rgba(255,255,255,0.35)', marginTop: '4px', maxWidth: '640px' },
  sectionTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', letterSpacing: '3px', color: 'rgba(255,255,255,0.45)', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  refHelp: { fontSize: '12px', color: 'rgba(255,255,255,0.35)', marginBottom: '12px', maxWidth: '720px' },
  code: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '11px', color: 'rgba(180,140,60,0.85)', backgroundColor: 'rgba(180,140,60,0.08)', padding: '1px 5px', borderRadius: '2px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', fontWeight: 600 },
  td: { padding: '10px 12px', color: '#ffffff', verticalAlign: 'middle' },
  numInput: { width: '90px', padding: '7px 10px', backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '3px', color: '#b48c3c', fontSize: '14px', fontWeight: 700, textAlign: 'right', outline: 'none', fontFamily: "'Barlow',sans-serif" },
  unit: { marginLeft: '6px', fontSize: '10px', color: 'rgba(255,255,255,0.3)', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '1px' },
  empty: { padding: '32px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '13px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '4px', border: '1px dashed rgba(255,255,255,0.08)' },
  stickyBar: { position: 'sticky', bottom: 0, left: 0, right: 0, marginTop: '24px', padding: '14px 18px', backgroundColor: 'rgba(10,15,26,0.94)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', zIndex: 5 },
  saveBtn: { padding: '11px 22px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px' },
}
